"use client";

import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ArrowRight, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import DegressiveGrid from "@/components/pricing/DegressiveGrid";
import PlanCard from "@/components/pricing/PlanCard";
import PlanToggle from "@/components/pricing/PlanToggle";
import QuantityInput from "@/components/pricing/QuantityInput";
import {
  formatEuro,
  formatEuroCents,
  formatEuroRate,
  NETWORK_DEFAULT_COMMUNITIES,
  NETWORK_MAX_COMMUNITIES,
  NETWORK_MIN_COMMUNITIES,
  networkAverageRate,
  networkBracketRates,
  networkTotal,
  SIGNATURE_DEFAULT_UNITS,
  SIGNATURE_MAX_UNITS,
  SIGNATURE_MIN_UNITS,
  SIGNATURE_UNITS_STEP,
  signatureAverageRate,
  signatureBase,
  signatureBracketRates,
  signatureTotal,
  type NetworkType,
  type SignatureType,
} from "@/components/pricing/pricingRates";

const CTA_CLASS = "h-auto w-full rounded-full px-6 py-3.5 text-[15px] font-bold";

const PriceTag = ({ amount, suffix }: { amount: string; suffix: string }) => (
  <div className="flex items-baseline gap-2">
    <span className="font-heading text-[40px] font-extrabold">{amount}</span>
    <span className="text-sm text-muted-foreground">{suffix}</span>
  </div>
);

const PricingSection = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [networkType, setNetworkType] = useState<NetworkType>("asso");
  const [communities, setCommunities] = useState(NETWORK_DEFAULT_COMMUNITIES);
  const [signatureType, setSignatureType] = useState<SignatureType>("coll");
  const [units, setUnits] = useState(SIGNATURE_DEFAULT_UNITS);

  const features = (plan: string) =>
    t(`pricing.plans.${plan}.features`, { returnObjects: true }) as string[];
  const ranges = (plan: string) =>
    t(`pricing.plans.${plan}.grid.rows`, { returnObjects: true }) as string[];

  // Collectivités are billed per inhabitant, everyone else per user
  const unitKind = signatureType === "coll" ? "inhabitants" : "users";

  return (
    <section id="pricing" className="relative overflow-hidden bg-muted/40 py-24">
      <div className="absolute right-1/4 top-20 h-72 w-72 bg-primary/15 blob-shape animate-blob-morph blur-3xl" />

      <div className="container relative mx-auto flex flex-col items-center gap-12 px-6">
        <div className="flex max-w-[760px] flex-col items-center gap-4 text-center">
          <h2 className="font-heading text-[32px] font-extrabold tracking-[-0.5px] sm:text-[42px]">
            <Trans
              i18nKey="pricing.title"
              components={{ gradient: <span className="text-gradient" /> }}
            />
          </h2>
          <p className="text-lg leading-[1.6] text-muted-foreground">
            {t("pricing.subtitle")}
          </p>
        </div>

        {/* Cards keep their natural height and stay vertically centred, so the
            shorter plans don't grow a gap between features and CTA */}
        <div className="grid w-full max-w-[1240px] items-center justify-center gap-6 [grid-template-columns:repeat(auto-fit,minmax(min(320px,100%),380px))]">
          <PlanCard
            name={t("pricing.plans.community.name")}
            tagline={t("pricing.plans.community.tagline")}
            features={features("community")}
            cta={
              <Button
                variant="outline"
                asChild
                className={`${CTA_CLASS} border-[1.5px] border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background`}
              >
                <a href={process.env.NEXT_PUBLIC_REGISTER_COMMUNITY_URL}>
                  {t("pricing.plans.community.cta")}
                </a>
              </Button>
            }
          >
            <PriceTag
              amount={formatEuro(0, locale)}
              suffix={t("pricing.plans.community.priceSuffix")}
            />
          </PlanCard>

          <PlanCard
            highlighted
            badge={t("pricing.badge")}
            name={t("pricing.plans.network.name")}
            tagline={t("pricing.plans.network.tagline")}
            features={features("network")}
            cta={
              <Button
                variant="hero"
                asChild
                className={`${CTA_CLASS} border-0 hover:shadow-xl hover:shadow-accent/50`}
              >
                <a href={process.env.NEXT_PUBLIC_REGISTER_NETWORK_URL}>
                  {t("pricing.plans.network.cta")}
                </a>
              </Button>
            }
          >
            <PlanToggle
              label={t("pricing.plans.network.typeLabel")}
              value={networkType}
              onChange={setNetworkType}
              options={[
                { value: "asso", label: t("pricing.plans.network.types.asso") },
                { value: "pro", label: t("pricing.plans.network.types.pro") },
              ]}
            />
            <div className="flex flex-col gap-1.5">
              <PriceTag
                amount={formatEuro(networkTotal(communities, networkType), locale)}
                suffix={t("pricing.perYear")}
              />
              <div className="flex flex-wrap items-center gap-1.5 text-[13.5px] text-muted-foreground">
                <span>{t("pricing.plans.network.quantityPrefix")}</span>
                <QuantityInput
                  label={t("pricing.plans.network.countLabel")}
                  value={communities}
                  min={NETWORK_MIN_COMMUNITIES}
                  max={NETWORK_MAX_COMMUNITIES}
                  onChange={setCommunities}
                  className="w-[58px]"
                />
                <span>
                  <Trans
                    i18nKey="pricing.plans.network.perCommunity"
                    values={{
                      unit: formatEuroCents(
                        networkAverageRate(communities, networkType),
                        locale,
                      ),
                    }}
                    components={{
                      strong: <strong className="text-foreground" />,
                    }}
                  />
                </span>
              </div>
              <DegressiveGrid
                summary={t("pricing.degressive")}
                note={t("pricing.bracketNote")}
                headers={[
                  t("pricing.plans.network.grid.header"),
                  t("pricing.plans.network.types.asso"),
                  t("pricing.plans.network.grid.pro"),
                ]}
                rows={networkBracketRates("asso").map(
                  (rate, index): [string, string, string] => [
                    ranges("network")[index],
                    formatEuro(rate, locale),
                    formatEuro(networkBracketRates("pro")[index], locale),
                  ],
                )}
              />
            </div>
          </PlanCard>

          <PlanCard
            name={t("pricing.plans.signature.name")}
            tagline={t("pricing.plans.signature.tagline")}
            features={features("signature")}
            cta={
              <Button
                variant="outline"
                asChild
                className={`${CTA_CLASS} border-[1.5px] border-accent bg-transparent text-accent hover:bg-accent hover:text-accent-foreground hover:shadow-lg hover:shadow-accent/40`}
              >
                <a href={process.env.NEXT_PUBLIC_BOOKING_URL}>
                  {t("pricing.plans.signature.cta")}
                </a>
              </Button>
            }
          >
            <PlanToggle
              label={t("pricing.plans.signature.typeLabel")}
              value={signatureType}
              onChange={setSignatureType}
              options={[
                { value: "coll", label: t("pricing.plans.signature.types.coll") },
                { value: "fede", label: t("pricing.plans.signature.types.nonProfit") },
                { value: "ent", label: t("pricing.plans.signature.types.ent") },
              ]}
            />
            <div className="flex flex-col gap-1.5">
              <PriceTag
                amount={formatEuro(signatureTotal(units, signatureType), locale)}
                suffix={t("pricing.perYear")}
              />
              <div className="flex flex-wrap items-center gap-1.5 text-[13.5px] text-muted-foreground">
                <span>{formatEuro(signatureBase(signatureType), locale)} +</span>
                <QuantityInput
                  label={t(`pricing.plans.signature.unitsLabel.${unitKind}`)}
                  value={units}
                  min={SIGNATURE_MIN_UNITS}
                  max={SIGNATURE_MAX_UNITS}
                  step={SIGNATURE_UNITS_STEP}
                  onChange={setUnits}
                  className="w-[104px]"
                />
                <span>
                  <Trans
                    i18nKey="pricing.plans.signature.perUnit"
                    values={{
                      unitWord: t(`pricing.plans.signature.unitWord.${unitKind}`),
                      unitSingular: t(
                        `pricing.plans.signature.unitSingular.${unitKind}`,
                      ),
                      rate: formatEuroRate(
                        signatureAverageRate(units, signatureType),
                        locale,
                      ),
                    }}
                    components={{
                      strong: <strong className="text-foreground" />,
                    }}
                  />
                </span>
              </div>
              <DegressiveGrid
                summary={t("pricing.degressive")}
                note={t("pricing.bracketNote")}
                headers={[
                  t(`pricing.plans.signature.grid.header.${unitKind}`),
                  t("pricing.plans.signature.grid.public"),
                  t("pricing.plans.signature.types.ent"),
                ]}
                rows={signatureBracketRates("coll").map(
                  (rate, index): [string, string, string] => [
                    ranges("signature")[index],
                    formatEuroRate(rate, locale),
                    formatEuroRate(signatureBracketRates("ent")[index], locale),
                  ],
                )}
              />
            </div>
          </PlanCard>
        </div>

        <p className="max-w-[680px] text-center text-sm leading-[1.6] text-muted-foreground">
          <Trans
            i18nKey="pricing.note"
            components={{
              contactLink: (
                <a
                  href={process.env.NEXT_PUBLIC_BOOKING_URL}
                  className="font-semibold text-[#D81E7D] underline decoration-transparent underline-offset-4 transition-colors hover:decoration-current dark:text-[#FADB5F]"
                />
              ),
            }}
          />
        </p>

        <div className="flex w-full max-w-4xl flex-col items-center gap-6 rounded-3xl border-2 border-dashed border-border bg-card p-8 transition-shadow duration-300 hover:shadow-xl hover:shadow-foreground/5 md:flex-row">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border-2 border-secondary/30 bg-secondary/15">
            <Server className="h-7 w-7 text-secondary" aria-hidden="true" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="mb-1 font-heading text-xl font-bold">
              {t("pricing.selfHost.title")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("pricing.selfHost.description")}
            </p>
          </div>
          <Button
            variant="outline"
            size="lg"
            asChild
            className="group h-auto flex-shrink-0 rounded-full px-6 py-3.5 text-[15px] font-bold"
          >
            <a
              href={process.env.NEXT_PUBLIC_GITLAB_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("pricing.selfHost.cta")}
              <ArrowRight
                className="transition-transform group-hover:translate-x-1"
                aria-hidden="true"
              />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
